import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, forwardRef, inject, input, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ButtonGroupComponent, type ButtonGroupOption } from '../button-group';
import { NumberInput } from '../number-input';
import { TimePicker } from '../time-picker';
import { DEFAULT_FULL_TIME_HOURS_PER_WEEK, DEFAULT_WORKING_DAYS_PER_WEEK } from '@zag/planning/common';
import { resolveWorkloadHoursPerDay, resolveWorkloadPercent } from '@zag/timetracking/common';

export interface TargetTimeValue {
  workloadHoursPerDay: number | null;
  workloadPercent: number | null;
}

type TargetTimeMode = 'hours' | 'percent';

function decimalToHhmm(decimal: number): string {
  const totalMinutes = Math.round(decimal * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function hhmmToDecimal(hhmm: string): number | null {
  const parts = hhmm.split(':');
  if (parts.length < 2) return null;
  const h = Number(parts[0]);
  const m = Number(parts[1]);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h + m / 60;
}

@Component({
  selector: 'zag-target-time-input',
  imports: [FormsModule, TranslatePipe, TimePicker, NumberInput, ButtonGroupComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TargetTimeInput),
      multi: true,
    },
  ],
  template: `
    <div class="target-time-input">
      <div class="target-time-input__field">
        @if (mode() === 'hours') {
          <zag-time-picker
            [allowDecimal]="true"
            [clearable]="false"
            [flushRight]="true"
            [disabled]="disabled()"
            [readonly]="readonly()"
            [placeholder]="'targetTime.hoursPlaceholder' | translate"
            [max]="maxHours()"
            [ngModel]="hoursPickerValue()"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onHoursPickerChange($event)"
          />
        } @else {
          <zag-number-input
            [min]="1"
            [max]="200"
            [step]="1"
            [clearable]="false"
            [flushRight]="true"
            [disabled]="disabled()"
            [readonly]="readonly()"
            [placeholder]="'targetTime.percentPlaceholder' | translate"
            suffix="%"
            [ngModel]="percentValue()"
            [ngModelOptions]="{ standalone: true }"
            (ngModelChange)="onPercentChange($event)"
          />
        }
      </div>
      <zag-button-group
        class="target-time-input__mode-toggle"
        orientation="horizontal"
        [bordered]="false"
        [options]="modeOptions()"
        [value]="mode()"
        [disabled]="disabled() || readonly()"
        [ariaLabel]="'targetTime.modeLabel' | translate"
        (selectionChange)="onModeSelect($event)"
      />
    </div>
  `,
  styles: `
    .target-time-input {
      display: flex;
      flex-direction: row;
      align-items: stretch;
      gap: 0;
    }

    .target-time-input__field {
      flex: 1 1 auto;
      min-width: 0;
    }

    .target-time-input__field zag-time-picker,
    .target-time-input__field zag-number-input {
      display: block;
      width: 100%;
    }

    /* Bare (borderless) toggle overlaps the input's right edge, hiding the
       input's rounded right corners. A single left divider separates the two,
       and the toggle rounds only its right corners so the whole control reads
       as one seamless input with a trailing segmented addon. */
    .target-time-input__mode-toggle {
      display: flex;
      flex: 0 0 auto;
      margin-left: -1px;
      border: var(--border-width-default) solid var(--input-border);
      border-top-right-radius: var(--border-radius-sm);
      border-bottom-right-radius: var(--border-radius-sm);
      overflow: hidden;
      background: var(--surface-color-default);
    }

    .target-time-input__mode-toggle:focus-within {
      position: relative;
      z-index: 1;
    }
  `,
})
export class TargetTimeInput implements ControlValueAccessor {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly translate = inject(TranslateService);

  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  readonly disabled = input<boolean>(false);
  readonly readonly = input<boolean>(false);
  /** Maximum hours per day (HH:MM string, e.g. "24:00"). Defaults to 24h cap. */
  readonly maxHours = input<string>('24:00');
  /** Full-time baseline hours/week used for hours ↔ percent conversion. */
  readonly fullTimeHoursPerWeek = input<number>(DEFAULT_FULL_TIME_HOURS_PER_WEEK);
  /** Working days per week used for hours ↔ percent conversion. */
  readonly workingDaysPerWeek = input<number>(DEFAULT_WORKING_DAYS_PER_WEEK);

  protected readonly mode = signal<TargetTimeMode>('hours');
  protected readonly hoursPickerValue = signal<string>('');
  protected readonly percentValue = signal<number | null>(null);

  protected readonly modeOptions = computed<ButtonGroupOption<TargetTimeMode>[]>(() => {
    this.currentLang(); // recompute labels on language change
    return [
      { value: 'hours', label: this.translate.instant('targetTime.mode.hours') },
      { value: 'percent', label: this.translate.instant('targetTime.mode.percent') },
    ];
  });

  protected readonly currentValue = computed<TargetTimeValue>(() => {
    if (this.mode() === 'hours') {
      const decimal = this.hoursPickerValue() ? hhmmToDecimal(this.hoursPickerValue()) : null;
      return { workloadHoursPerDay: decimal, workloadPercent: null };
    }
    return { workloadHoursPerDay: null, workloadPercent: this.percentValue() };
  });

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: TargetTimeValue) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  writeValue(value: TargetTimeValue | null): void {
    if (!value) {
      this.hoursPickerValue.set('');
      this.percentValue.set(null);
      this.cdr.markForCheck();
      return;
    }
    if (value.workloadHoursPerDay !== null) {
      this.mode.set('hours');
      this.hoursPickerValue.set(decimalToHhmm(value.workloadHoursPerDay));
    } else if (value.workloadPercent !== null) {
      this.mode.set('percent');
      this.percentValue.set(value.workloadPercent);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: TargetTimeValue) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(_isDisabled: boolean): void {
    this.cdr.markForCheck();
  }

  protected onModeSelect(mode: TargetTimeMode | undefined): void {
    if (!mode || mode === this.mode()) return;
    if (mode === 'percent') {
      const decimal = this.hoursPickerValue() ? hhmmToDecimal(this.hoursPickerValue()) : null;
      const percent = Math.round(resolveWorkloadPercent(decimal, null, this.fullTimeHoursPerWeek(), this.workingDaysPerWeek()) * 100) / 100;
      this.percentValue.set(percent || null);
    } else {
      const percent = this.percentValue();
      const hoursPerDay = resolveWorkloadHoursPerDay(null, percent, this.fullTimeHoursPerWeek(), this.workingDaysPerWeek());
      this.hoursPickerValue.set(hoursPerDay > 0 ? decimalToHhmm(hoursPerDay) : '');
    }
    this.mode.set(mode);
    this.emit();
  }

  protected onHoursPickerChange(hhmm: string): void {
    this.hoursPickerValue.set(hhmm);
    this.emit();
    this.onTouched();
  }

  protected onPercentChange(value: number | null): void {
    this.percentValue.set(value);
    this.emit();
    this.onTouched();
  }

  private emit(): void {
    this.onChange(this.currentValue());
  }
}
